import { SEO_CATEGORIES, SEO_REGIONS, type LangCode } from "../app/data/regions";

// How a listing's title and place read on cards, the map and the detail page.
// Sellers' titles arrive as typed — emoji, ALL CAPS, the whole description in
// the title — and older auto-titles are frozen in whatever language the
// seller used ("2-бөлм., Батир" next to "3-комн., Дом"). This makes them
// consistent without touching what's stored.

export const TITLE_MAX = 70;

type T = (key: string, opts?: any) => string;

const langOf = (lang?: string): LangCode => (((lang || "ru").split("-")[0] as LangCode) || "ru");

/** Region in the UI language ("TALAS" → "Талас"). */
export function regionLabel(region: string | undefined, lang?: string) {
  if (!region) return "";
  const r = SEO_REGIONS.find((x) => x.value === region);
  const l = langOf(lang);
  return r?.names[l] ?? r?.names.ru ?? region.charAt(0) + region.slice(1).toLowerCase();
}

/** Property type in the UI language, short form for cards. */
export function typeLabel(type: string | undefined, t: T, lang?: string) {
  const v = String(type || "").toUpperCase();
  if (v === "COMMERCIAL") return t("propertyMap.commercialShort", { defaultValue: "Коммерция" });
  const c = SEO_CATEGORIES.find((x) => x.value === v);
  const l = langOf(lang);
  return c?.names[l] ?? c?.names.ru ?? "";
}

/** Plots are measured in sotka (1 сотик = 100 м²) — the form asks for sotka. */
export const isPlotType = (type?: string) => ["PLOT", "LAND"].includes(String(type || "").toUpperCase());

/** "65 м²" for flats, "6.5 сотик" for plots — the one place the unit is chosen. */
export function areaText(area: number | string | undefined | null, type: string | undefined, t: T) {
  const v = String(area ?? "").trim();
  if (!v || !(Number(v.replace(",", ".")) > 0)) return "";
  return `${v} ${isPlotType(type) ? t("listing.sotok", { defaultValue: "сот." }) : t("common.sqm", { defaultValue: "м²" })}`;
}

/** Rooms only make sense for flats and houses. */
export const hasRooms = (type?: string) => !["COMMERCIAL", "PLOT", "LAND"].includes(String(type || "").toUpperCase());

// Abbreviations that stay upper-case when a CAPS title is softened.
const KEEP_CAPS = new Set(["ПСО", "ЦУМ", "ГУМ", "ТРЦ", "ТЦ", "БЦ", "ИП", "ОсОО", "ОАО", "ЖСК", "VIP", "ТОП", "USD", "KGS"]);

const EMOJI = /[\p{Extended_Pictographic}\u{1F1E6}-\u{1F1FF}\u{FE0F}\u{200D}\u{20E3}]/gu;

export const stripEmoji = (s: string) => s.replace(EMOJI, "").replace(/\s{2,}/g, " ");

/** Mostly upper-case letters (≥ 6 letters, > 70 % caps). */
export function isShouting(s: string) {
  const letters = String(s || "").replace(/[^\p{L}]/gu, "");
  const upper = letters.replace(/[^\p{Lu}]/gu, "");
  return letters.length >= 6 && upper.length / letters.length > 0.7;
}

/** Emoji out, ALL-CAPS softened (short all-caps words like ЖК / ПСО kept), max length. */
export function cleanTitle(raw: string | undefined, max = TITLE_MAX) {
  let s = String(raw ?? "")
    .replace(EMOJI, " ")
    .replace(/[!]{2,}/g, "!")
    .replace(/\s+/g, " ")
    .replace(/^[\s.,:;!–—-]+|[\s,:;–—-]+$/g, "")
    .trim();
  const letters = s.replace(/[^\p{L}]/gu, "");
  const upper = letters.replace(/[^\p{Lu}]/gu, "");
  if (letters.length >= 6 && upper.length / letters.length > 0.7) {
    s = s
      .split(" ")
      .map((w) => {
        const bare = w.replace(/[^\p{L}]/gu, "");
        // Two-letter abbreviations (ЖК, ТЦ) stay; single letters are prepositions.
        return bare.length === 2 || KEEP_CAPS.has(bare) ? w : w.toLowerCase();
      })
      .join(" ");
    s = s.charAt(0).toLocaleUpperCase() + s.slice(1);
  }
  if (max && s.length > max) {
    const cut = s.slice(0, max);
    const at = cut.lastIndexOf(" ");
    s = (at > max * 0.6 ? cut.slice(0, at) : cut).replace(/[\s,.;:–—-]+$/, "") + "…";
  }
  return s;
}

// "2-бөлм., Батир, 65 м², Бишкек" / "3-комн., Дом, 112 м², Бишкек" — titles the
// create form used to generate automatically.
const AUTO_TITLE = /^\s*\d+\s*-\s*(комн|бөлм|бөлмө|xona|oda|rm)\.?\s*,\s*[^,]+,\s*[\d.,]+\s*м²/i;

export function isAutoTitle(title?: string) {
  return !!title && AUTO_TITLE.test(title);
}

interface TitleInput {
  title?: string;
  type?: string;
  category?: string;
  room_count?: number | string;
  rooms_count?: number | string;
  area?: number | string;
  region?: string;
  district?: string;
}

/** "2-комн., Квартира, 65 м², Бишкек" rebuilt in the UI language. */
export function autoTitle(p: TitleInput, t: T, lang?: string) {
  const type = p.type || p.category;
  const rooms = Number(p.room_count ?? p.rooms_count) || 0;
  const area = Number(String(p.area ?? "").replace(",", ".")) || 0;
  const district = p.district && p.district !== "bishkek" ? t(`districts.${p.district}`, { defaultValue: "" }) : "";
  return [
    hasRooms(type) && rooms ? `${rooms}-${t("listing.roomsShort", { defaultValue: "комн." })}` : "",
    typeLabel(type, t, lang),
    area ? areaText(area, type, t) : "",
    district || regionLabel(p.region, lang),
  ]
    .filter(Boolean)
    .join(", ");
}

/** The title to show: auto-titles re-rendered, everything else cleaned. */
export function displayTitle(p: TitleInput, t: T, lang?: string, max = TITLE_MAX) {
  if (isAutoTitle(p.title)) return autoTitle(p, t, lang);
  const clean = cleanTitle(p.title, max);
  return clean || autoTitle(p, t, lang);
}

/**
 * Street-level address instead of the geocoder dump:
 * "Чүй, 1/3, микрорайон Джал-15, Ленинский район, город Бишкек, 720007, Кыргызстан"
 * → "Джал-15, 1/3". The city / region is shown next to it separately.
 */
export function shortAddress(raw: string | undefined) {
  const parts = String(raw ?? "")
    .split(/\n/)[0]
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
  const drop = (x: string) =>
    /^(кыргызстан|киргизия|kyrgyzstan|кыргыз республикасы)$/i.test(x) ||
    /^\d{5,6}$/.test(x) ||
    /^(город|шаары|г\.)\s/i.test(x) ||
    /(область|облусу|район|району|аймагы)$/i.test(x) ||
    /^(чүй|чуй|бишкек|ош|нарын|талас|баткен|жалал-абад|джалал-абад|ысык-көл|иссык-куль)$/i.test(x);
  const kept = parts
    .filter((x) => !drop(x))
    .map((x) => x.replace(/^(микрорайон|мкр\.?|кичирайон|жилмассив|ж\/м)\s+/i, ""));
  // A lone house number ("1/3") reads better after its street / neighbourhood.
  if (kept.length > 1 && /^[\d/\-а-яa-z]{1,6}$/i.test(kept[0]) && /\d/.test(kept[0])) kept.push(kept.shift()!);
  return kept.slice(0, 2).join(", ");
}

/**
 * Is a seller's title written in another language than the interface?
 * («2-Bedroom Apartment for Sale» on the Kyrgyz site, a Kyrgyz title on the
 * Russian one.) Script-level only — cheap, no request — so the detail page
 * offers a translation just where it obviously helps.
 */
export function titleNeedsTranslation(title: string | undefined, lang: string) {
  const s = String(title ?? "");
  const letters = s.replace(/[^\p{L}]/gu, "");
  if (letters.length < 4) return false;
  const share = (re: RegExp) => (letters.match(re)?.length ?? 0) / letters.length;
  const latin = share(/[A-Za-z\u00C0-\u024F]/g);
  const cyrillic = share(/[\u0400-\u04FF]/g);
  const cjk = share(/[\u4E00-\u9FFF]/g);
  const l = (lang || "kg").split("-")[0];
  if (l === "zh") return cjk < 0.3;
  if (l === "en" || l === "tr" || l === "uz") return cyrillic > 0.5;
  // kg / ru / kz: Latin or Chinese titles, or the other Cyrillic language.
  if (latin > 0.5 || cjk > 0.3) return true;
  const kyrgyz = /[ңөүҢӨҮ]/.test(s);
  if (l === "ru") return kyrgyz || /[әғқұһіӘҒҚҰҺІ]/.test(s);
  if (l === "kg") return !kyrgyz && /(^|[^\p{L}])(продаю|прода[её]тся|квартир|комнат|дом|участок|сда[её]тся)/iu.test(s);
  return false;
}
